import { Box, Chip, IconButton, Typography } from "@mui/material"
import { ChatOutlined, MoreVertOutlined } from "@mui-symbols-material/w300"
import React, { useCallback, useState } from "react"
import type { OperationContext } from "urql"

import CompanyHeaderDropdownMenu from "../../components/CompanyHeaderDropdownMenu"
import { NoLocationActivationModal } from "../../components/NoLocationActivationModal"
import { useCompanyStatusUpdate } from "../../hooks"
import { useCompanyTypesStore } from "../../stores"
import { findCompanyType, formatMeasurementSystemUoM, getCompanyStatusColor } from "../../utils/company"

import { CommentsListDialog } from "@/components/Comment/CommentsListDialog"
import DisplayIds from "@/components/common/DisplayIds/DisplayIds"
import { DATE_FORMAT } from "@/constants/formats"
import { PermissionActionType } from "@/constants/permissions"
import type { Company, CompanyUpdateFailure, CustomerAccountStatus } from "@/graphql/codegen/graphql"
import { CommentParentTableType, CompanyStatus, ExternalLocationType } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { NotificationType } from "@/schemas/notification"
import CustomerAccountStatusMenu from "@/screens/Companies/components/CustomerAccountStatusMenu"
import { useCompanyCustomerAccountMutation } from "@/screens/Companies/hooks/useCompanyCustomerAccountMutation"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { CUSTOMER_ACCOUNT_STATUS_COMPANY_TYPES, CUSTOMER_COMPANY_TYPES, type CompanyType } from "@/types/companyType"
import { formatDate } from "@/utils/date"

interface CompanyDetails {
  company: Company
  name: string
  status: CompanyStatus
  types?: string[]
  onEditClick: (value: boolean) => void
  locationsCount: number
  onCompanyRefresh: (opts?: Partial<OperationContext>) => void
}

const CompanyHeader: React.FC<CompanyDetails> = (props) => {
  const { company, name, status, types = [], onEditClick, locationsCount, onCompanyRefresh } = props

  const {
    companyId,
    externalSystemId,
    customerExternalSystemId,
    publicId,
    defaultUom,
    preferredMeasurementSystem,
    accountManagers = [],
    productManagers = [],
  } = company

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false)
  const [showNoLocationModal, setShowNoLocationModal] = useState(false)
  const companyTypes = useCompanyTypesStore((state) => state.companyTypes)
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const getChipName = useCallback((type: string) => findCompanyType(companyTypes, type)?.name, [companyTypes])
  const { updateCompanyStatus, isUpdating } = useCompanyStatusUpdate()
  const { updateCustomerAccountStatus } = useCompanyCustomerAccountMutation()
  const { hasPermissions } = usePermissionManager()

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleActivate = async () => {
    if (isUpdating) return

    const result = await updateCompanyStatus({
      companyId,
      status: CompanyStatus.Active,
      locationsCount,
      types,
    })
    if (result.success) {
      handleCloseMenu()
    } else if (result.noLocations) {
      setShowNoLocationModal(true)
    }
    onCompanyRefresh()
  }

  const handleDeactivate = async () => {
    if (isUpdating) return
    const result = await updateCompanyStatus({
      companyId,
      status: CompanyStatus.Inactive,
      locationsCount,
      types,
    })
    if (result.success) {
      handleCloseMenu()
    }
    onCompanyRefresh()
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const handleEdit = () => {
    onEditClick(true)
    handleCloseMenu()
  }

  const handleOpenCommentHistory = () => {
    setIsCommentDialogOpen(true)
  }

  const handleChangeCustomerAccountStatus = async (status: CustomerAccountStatus) => {
    const { error, data: result } = await updateCustomerAccountStatus({
      input: {
        companyId,
        customerAccountStatus: status,
        types,
      },
    })

    if (error || (result?.company?.update as CompanyUpdateFailure)?.error) {
      notify({
        type: NotificationType.Error,
        content: "Update customer account status failed.",
      })
    } else {
      onCompanyRefresh()

      notify({
        type: NotificationType.Success,
        content: "Customer account status successfully updated.",
      })
    }
  }

  const isActive = status === CompanyStatus.Active
  const isCustomerCompanyType = types?.some((item) => CUSTOMER_COMPANY_TYPES.includes(item as CompanyType))
  const hasCustomerPermission = hasPermissions([PermissionActionType.CUSTOMER_VIEW])

  const hideCommentsHistory = status === CompanyStatus.Inactive || status === CompanyStatus.Pending

  const accountOpenDate = company.locations?.find(
    (location) => location?.externalLocationType === ExternalLocationType.Bill
  )?.accountOpenDate

  return (
    <>
      {isCommentDialogOpen && (
        <CommentsListDialog
          onClose={() => setIsCommentDialogOpen(false)}
          title={name}
          commentParentTableType={CommentParentTableType.Company}
          referenceId={companyId}
        />
      )}
      <NoLocationActivationModal
        open={showNoLocationModal}
        onClose={() => setShowNoLocationModal(false)}
        companyId={companyId}
      />
      <Box className='flex justify-between rounded-md border border-solid border-gray-300 p-6'>
        <Box>
          <Box className='flex items-center justify-between gap-3'>
            <Box className='flex items-center gap-3'>
              <Typography variant='h5' className='text-gray-800'>
                {name}
              </Typography>
              <Chip label={status} variant='outlined' color={getCompanyStatusColor(status)} />
              {publicId && (externalSystemId || customerExternalSystemId) && (
                <DisplayIds
                  entityName='Company ID'
                  publicId={publicId}
                  externalSystemId={String(externalSystemId ?? customerExternalSystemId)}
                />
              )}

              {types.some((item) => CUSTOMER_ACCOUNT_STATUS_COMPANY_TYPES.includes(item as CompanyType)) && (
                <CustomerAccountStatusMenu
                  onChangeCustomerAccountStatus={handleChangeCustomerAccountStatus}
                  customerStatus={company.customerAccountStatus}
                />
              )}
            </Box>
          </Box>
          <Box className='mt-6 flex flex-wrap items-center gap-8'>
            <Box className='flex flex-wrap items-center gap-2'>
              <Typography variant='body2' className='text-gray-700'>
                Company Type:
              </Typography>
              {(types ?? []).map((type) => {
                const label = getChipName(type)
                return <Chip key={type} label={label} className='rounded-sm font-[13px]' />
              })}
            </Box>

            {accountOpenDate && isCustomerCompanyType && hasCustomerPermission && (
              <Box className='flex flex-wrap items-center gap-2'>
                <Typography variant='body2' className='text-gray-700'>
                  Account Opened:
                </Typography>
                <Chip
                  key='company-account-open-date'
                  data-testid='company-account-open-date'
                  label={formatDate(accountOpenDate, DATE_FORMAT.COMPANY_HEADER)}
                  className='rounded-sm font-[13px]'
                />
              </Box>
            )}

            <Box className='flex flex-wrap items-center gap-2'>
              <Typography variant='body2' className='text-gray-700'>
                System / UoM:
              </Typography>

              <Chip
                label={formatMeasurementSystemUoM(preferredMeasurementSystem, defaultUom ?? "")}
                className='rounded-sm bg-primary-100'
              />
            </Box>
          </Box>
          {!isCustomerCompanyType && (
            <Box className='mt-4 flex flex-wrap items-center justify-between'>
              <Box className='flex flex-wrap items-center gap-2'>
                <Typography variant='body2' className='text-gray-700'>
                  Account Manager(s):
                </Typography>
                {accountManagers.length > 0 ? (
                  accountManagers.map(({ firstName, lastName }) => (
                    <Chip
                      key={`${firstName}-${lastName}`}
                      label={`${firstName} ${lastName}`}
                      className='rounded-sm bg-primary-100'
                    />
                  ))
                ) : (
                  <Chip label='None' className='rounded-sm bg-primary-100' />
                )}
              </Box>
            </Box>
          )}
          <Box className='mt-4 flex flex-wrap items-center justify-between'>
            <Box className='flex flex-wrap items-center gap-2'>
              <Typography variant='body2' className='text-gray-700'>
                Product Manager(s):
              </Typography>
              {productManagers?.map(({ firstName, lastName }) => (
                <Chip
                  key={`${firstName}-${lastName}`}
                  label={`${firstName} ${lastName}`}
                  className='rounded-sm bg-tertiary-yellow-100'
                />
              )) ?? <Chip label='None' className='rounded-sm bg-tertiary-yellow-100' />}
            </Box>
          </Box>
        </Box>
        <Box className='flex flex-col justify-between'>
          <IconButton color='primary' aria-label='company actions' onClick={handleOpenMenu}>
            <MoreVertOutlined />
          </IconButton>
          <CompanyHeaderDropdownMenu
            anchorEl={anchorEl}
            onClose={handleCloseMenu}
            isActive={isActive}
            onEdit={handleEdit}
            onDeactivate={handleDeactivate}
            onActivate={handleActivate}
            disabled={isUpdating}
          />

          {!hideCommentsHistory && (
            <IconButton color='primary' onClick={handleOpenCommentHistory} aria-label='Open comment history'>
              <ChatOutlined />
            </IconButton>
          )}
        </Box>
      </Box>
    </>
  )
}

export default CompanyHeader
